import api from './api';
import { 
  User, 
  AuthResponse, 
  LoginDto, 
  RegisterDto, 
  ResetPasswordRequestDto, 
  ResetPasswordDto, 
  ChangePasswordDto, 
  VerifyEmailDto, 
  RefreshTokenDto, 
  RefreshTokenResponse 
} from '../types';

const TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';
const USER_KEY = 'user';

export const authService = {
  async login(credentials: LoginDto): Promise<AuthResponse> {
    const response = await api.post('/auth/login', credentials);
    const { access_token, refresh_token, user } = response.data;
    
    this.setTokens(access_token, refresh_token);
    this.setUser(user);
    
    return response.data;
  },

  async register(userData: RegisterDto): Promise<AuthResponse> {
    const response = await api.post('/auth/register', userData);
    const { access_token, refresh_token, user } = response.data;
    
    this.setTokens(access_token, refresh_token);
    this.setUser(user);
    
    return response.data;
  },

  async verifyEmail(token: string): Promise<{ message: string; user: { email: string; emailVerified: boolean } }> {
    const response = await api.post('/auth/verify-email', { token });
    return response.data;
  },

  async resendVerificationEmail(email: string): Promise<{ message: string }> {
    const response = await api.post('/auth/resend-verification', { email });
    return response.data;
  },

  async requestPasswordReset(email: string): Promise<{ message: string }> {
    const response = await api.post('/auth/reset-password-request', { email });
    return response.data;
  },

  async resetPassword(resetData: ResetPasswordDto): Promise<{ message: string }> {
    const response = await api.post('/auth/reset-password', resetData);
    return response.data;
  },

  async changePassword(changeData: ChangePasswordDto): Promise<{ message: string }> {
    const response = await api.patch('/auth/change-password', changeData);
    return response.data;
  },

  async refreshToken(): Promise<RefreshTokenResponse> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const response = await api.post('/auth/refresh-token', { refreshToken });
    const { access_token, refresh_token } = response.data;
    
    this.setTokens(access_token, refresh_token);
    
    return response.data;
  },

  async getProfile(): Promise<User> {
    const response = await api.get('/auth/me');
    this.setUser(response.data);
    return response.data;
  },

  async logout(refreshToken?: string): Promise<void> {
    try {
      const token = refreshToken || this.getRefreshToken();
      if (token) {
        await api.post('/auth/logout', { refreshToken: token });
      }
    } catch (error) {
      console.error('Logout API call failed:', error);
    } finally {
      this.clearTokens();
      window.location.href = '/login';
    }
  },

  // Token management
  setTokens(accessToken: string, refreshToken?: string): void {
    localStorage.setItem(TOKEN_KEY, accessToken);
    if (refreshToken) {
      localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    }
  },

  clearTokens(): void {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem('sidebarOpen'); // Clear sidebar state on logout
  },

  getToken(): string | null {
    return localStorage.getItem(TOKEN_KEY);
  },

  getRefreshToken(): string | null {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  },

  // User management
  setUser(user: User): void {
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  },

  getCurrentUser(): User | null {
    const userStr = localStorage.getItem(USER_KEY);
    return userStr ? JSON.parse(userStr) : null;
  },

  updateCurrentUser(updates: Partial<User>): void {
    const currentUser = this.getCurrentUser();
    if (currentUser) {
      const updatedUser = { ...currentUser, ...updates };
      this.setUser(updatedUser);
    }
  },

  // Auth state checks
  isAuthenticated(): boolean {
    return !!this.getToken();
  },

  isEmailVerified(): boolean {
    const user = this.getCurrentUser();
    return user?.emailVerified || false;
  },

  hasRole(role: string): boolean {
    const user = this.getCurrentUser();
    return user?.roles?.includes(role) || false;
  },

  isAdmin(): boolean {
    return this.hasRole('admin');
  },

  isModerator(): boolean {
    return this.hasRole('moderator') || this.hasRole('admin');
  },
};
